// Message display: "cozy" (avatars, the name above the text), "compact" (Discord's compact mode: no avatars, the
// time and name inline with the text), or "bubbles" (chat bubbles: others on the left, mine on the right). Saved to
// the account like the theme (/api/me/settings), and in localStorage so it applies before the first paint on this
// device. Applied as <html data-density>, so the CSS does the layout.
import { useSyncExternalStore } from "react";
import { MESSAGE_DISPLAYS, type MessageDisplay } from "@bloom/shared";
import { api } from "./api.ts";

const KEY = "bloom:message-display";
const listeners = new Set<() => void>();

/** A stored value as a message display; anything else is "cozy". */
export function parseMessageDisplay(raw: unknown): MessageDisplay {
  return MESSAGE_DISPLAYS.includes(raw as MessageDisplay) ? (raw as MessageDisplay) : "cozy";
}

function stored(): MessageDisplay {
  try {
    return parseMessageDisplay(localStorage.getItem(KEY));
  } catch {
    return "cozy";
  }
}

let pref: MessageDisplay = stored();

function apply() {
  if (typeof document !== "undefined") document.documentElement.dataset.density = pref;
  for (const fn of listeners) fn();
}

/** Sets it on this device right away; `save` also stores it on the account for my other devices. */
export function setMessageDisplay(next: MessageDisplay, { save = true } = {}) {
  if (!MESSAGE_DISPLAYS.includes(next)) return;
  pref = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // private mode: still applies for this page
  }
  apply();
  if (save) void api("/me/settings", { method: "PUT", body: { messageDisplay: next } }).catch(() => {});
}

export const getMessageDisplay = () => pref;

export function installDensity() {
  apply();
  // Another tab changed it.
  addEventListener("storage", (e) => {
    if (e.key !== KEY) return;
    pref = stored();
    apply();
  });
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useMessageDisplay = (): MessageDisplay => useSyncExternalStore(subscribe, () => pref);
